import React, { FC, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import classNames from "classnames";
import moment from "moment";
import { IBriefNote } from "../constants";
import { BriefNoteMenu } from "./BriefNoteMenu";
import { BRIEF_NOTE_MAX_LENGTH } from "./useBriefNotes";
import { DotLottleLoader } from "../../Loader";
import { IconCheck, IconCross } from "../../../utils/icons";
import {
    MentionText,
    TaggableUser,
    existingMentions,
    serializeMentions,
    toDraft,
} from "../../Mentions/MentionText";
import { MentionTextarea } from "../../Mentions/MentionTextarea";

interface Props {
    note: IBriefNote;
    /** The viewer's own, non-deleted note: shows the ⋯ menu. */
    canManage: boolean;
    focused: boolean;
    taggableUrl: string | null;
    onUpdate: (noteId: number, message: string) => Promise<boolean>;
    onDelete: (noteId: number) => Promise<boolean>;
}

export const briefNoteElementId = (noteId: number | string) => `brief-note-${noteId}`;

const getAuthorName = (note: IBriefNote) => {
    const { user } = note;
    if (user?.displayName) return user.displayName;
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    if (fullName) return fullName;
    return note.guest_name || "Unknown user";
};

const getGreyText = (text: string) => (
    <Typography variant="body2" className="!text-xs text-text-2">
        {text}
    </Typography>
);

// One note card (copied from the video-review CommentList card, minus the
// video range chip). Escape cancels an edit instead of saving it.
export const BriefNoteItem: FC<Props> = ({
    note,
    canManage,
    focused,
    taggableUrl,
    onUpdate,
    onDelete
}) => {
    const [draft, setDraft] = useState<string | null>(null);
    const [picked, setPicked] = useState<TaggableUser[]>([]);
    const [busy, setBusy] = useState(false);
    const editRef = useRef<HTMLTextAreaElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    // Set when an edit ends by Cancel/Escape/Save, so keyboard focus goes back
    // to this note's ⋯ button instead of falling to the dialog.
    const restoreFocusRef = useRef(false);
    const isMounted = useRef(true);
    useEffect(
        () => () => {
            isMounted.current = false;
        },
        []
    );

    const isEditing = draft !== null;

    // The note went away under an open edit.
    useEffect(() => {
        if (!canManage) setDraft(null);
    }, [canManage]);

    useEffect(() => {
        if (!isEditing && restoreFocusRef.current) {
            restoreFocusRef.current = false;
            cardRef.current?.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')?.focus();
            return;
        }
        const el = editRef.current;
        if (!isEditing || !el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
    }, [isEditing]);

    const cancelEdit = () => {
        restoreFocusRef.current = true;
        setDraft(null);
        // Picks from an abandoned edit must not turn a plain @Name in the
        // next one into a mention.
        setPicked([]);
    };

    const submitEdit = async () => {
        const draftText = (draft || "").trim();
        if (!draftText) return;
        const message = serializeMentions(draftText, [
            ...existingMentions(note.message),
            ...picked,
        ]);
        if (message === note.message.trim()) {
            cancelEdit();
            return;
        }
        setBusy(true);
        const ok = await onUpdate(note.id, message);
        if (!isMounted.current) return;
        setBusy(false);
        // On failure the hook has toasted; keep the edit open with the text.
        if (ok) cancelEdit();
    };

    const handleDelete = async () => {
        setBusy(true);
        await onDelete(note.id);
        if (isMounted.current) setBusy(false);
    };

    let tag: React.ReactNode = null;
    // Soft-deleted notes stay in the (internal-only) thread, tagged.
    if (note.deleted) {
        tag = <span className="text-xs text-red-400 ml-1">Deleted</span>;
    } else if (note.edited) {
        tag = <span className="text-xs text-muted ml-1">Edited</span>;
    }

    return (
        <Box id={briefNoteElementId(note.id)} tabIndex={focused ? -1 : undefined} className="w-full flex outline-none">
            <Box
                ref={cardRef}
                className={classNames(
                    "border rounded-2xl flex flex-col gap-4 p-4 pb-5 relative bg-surface w-full min-w-0 transition-shadow duration-500",
                    focused ? "border-accent ring-2 ring-accent" : "border-border-token"
                )}
            >
                <Box className="flex flex-col w-full gap-2">
                    <Box className="flex items-start gap-2 justify-between">
                        <Typography variant="body1" className="min-w-0 break-words">
                            {getAuthorName(note)}
                            {tag}
                        </Typography>
                        {canManage && !isEditing && (
                            <Box className="flex items-center gap-1 shrink-0">
                                <BriefNoteMenu
                                    noteId={note.id}
                                    onEdit={() => setDraft(toDraft(note.message))}
                                    onDelete={handleDelete}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box className="flex gap-2 items-center">
                        {isEditing ? (
                            <>
                                <MentionTextarea
                                    ref={editRef}
                                    value={draft || ""}
                                    maxLength={BRIEF_NOTE_MAX_LENGTH}
                                    ariaLabel="Edit note"
                                    usersUrl={taggableUrl}
                                    onMentionPicked={(user) =>
                                        setPicked((prev) =>
                                            prev.some((p) => p.userid === user.userid)
                                                ? prev
                                                : [...prev, user],
                                        )
                                    }
                                    className="border border-border-token rounded-md p-2 w-full text-xs py-1 bg-surface text-text-1 outline-0 focus:border-accent resize-none"
                                    onChange={setDraft}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape") {
                                            // Cancel this edit only — not the whole brief dialog.
                                            e.stopPropagation();
                                            cancelEdit();
                                        }
                                    }}
                                    minRows={1}
                                    disabled={busy}
                                />
                                <Box className="flex gap-1 items-center">
                                    <IconButton
                                        aria-label="Cancel edit"
                                        className="!p-0 !bg-surface !border !border-text-1 !rounded-2xl w-[28px] h-[28px] items-center justify-center"
                                        sx={{ border: 1, color: "var(--text)" }}
                                        onClick={cancelEdit}
                                        disabled={busy}
                                    >
                                        {/* currentColor: an SVG stroke attribute can't read var() */}
                                        <IconCross width={20} height={20} stroke="currentColor" />
                                    </IconButton>
                                    <IconButton
                                        aria-label="Save note"
                                        className="!p-0 !bg-accent !rounded-2xl w-[28px] h-[28px] [&.Mui-disabled]:opacity-40"
                                        onClick={submitEdit}
                                        disabled={busy || !(draft || "").trim()}
                                    >
                                        <IconCheck width={24} height={24} stroke="#fff" />
                                    </IconButton>
                                </Box>
                            </>
                        ) : (
                            <Typography
                                variant="body2"
                                className="!text-xs text-text-2 w-full whitespace-pre-wrap break-words"
                            >
                                <MentionText text={note.message} />
                            </Typography>
                        )}
                    </Box>
                </Box>
                {getGreyText(moment(note.created_at).format("LT"))}
                {busy && (
                    <Box
                        className="absolute inset-0 flex items-center justify-center rounded-2xl"
                        sx={{
                            backgroundColor: "rgba(255,255,255,0.5)",
                            '[data-theme="dark"] &': { backgroundColor: "rgba(24,33,47,0.5)" }
                        }}
                    >
                        <DotLottleLoader sx={{ width: 60, height: 60 }} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
